import { Check, Star, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { CAMPAIGNS, calculateDiscountedPrice, type SubscriptionTier } from '@/api/subscriptions';
import { type PolarDiscount } from '@/api/polar';
import CTAButton from "@/components/marketing/CTAButton";
import { Badge } from '@/components/ui/badge';

interface PricingCardProps {
  tier: SubscriptionTier;
  isRecommended?: boolean;
  isWeekly?: boolean;
  ctaLink?: string;
  ctaText?: string;
  ctaVariant?: 'default' | 'outline';
  onCtaClick?: () => void;
  useCTAButton?: boolean; // Use CTAButton component (for landing pages) vs regular Button
  isCampaignActive?: boolean; // Override campaign active status (from useCampaignStatus hook)
  polarDiscount?: PolarDiscount | null; // Actual Polar discount data for accurate pricing
}

export function PricingCard({
  tier,
  isRecommended = false,
  isWeekly = false,
  ctaLink = '/?auth=signup',
  ctaText = 'Start Writing',
  ctaVariant,
  onCtaClick,
  useCTAButton = true,
  isCampaignActive = true,
  polarDiscount,
}: PricingCardProps) {
  // Only show campaign if it's active and exists
  const showCampaign = tier.campaign && CAMPAIGNS[tier.campaign] && isCampaignActive;

  // Calculate pricing using actual Polar discount data
  const pricing = calculateDiscountedPrice(tier, showCampaign ? polarDiscount ?? null : null);
  const variant = ctaVariant || (isRecommended ? 'default' : 'outline');

  return (
    <Card
      className={`relative overflow-hidden rounded-3xl h-full flex flex-col ${isRecommended
        ? 'glass-card shadow-xl border-primary/30'
        : ''
        }`}
    >
      {/* Background decoration for recommended card */}
      {isRecommended && (
        <>
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/8 dark:bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/8 dark:bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/4 dark:bg-primary/5 rounded-full blur-2xl" />
        </>
      )}

      {/* Recommended badge - top right */}

      <CardContent className={`relative flex flex-col flex-1 p-6`}>
        <div className="space-y-5">
          {/* Icon badge and header */}
          <div className="space-y-4 relative">
            <div className="flex items-center justify-between">

              <h3 className="text-4xl font-bold text-foreground">
                {tier.name}
              </h3>
              {isRecommended && (
                <Badge variant="glass" className="text-primary dark:text-white text-xs font-semibold flex items-center gap-1">
                  <Star className="w-4 h-4" /> Best Value
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-lg">
              {tier.description}
            </p>
          </div>

          {/* Pricing */}
          <div>
            <div className="flex flex-col items-baseline gap-2">
              {pricing.hasDiscount && (
                <>
                  <span className="text-5xl font-bold font-serif text-foreground flex items-center">
                    ${pricing.discountedPrice}<span className="flex flex-col ml-2"><span className="text-base opacity-75">/{tier.billingPeriod || 'month'}

                    </span>
                      <small className="text-xs opacity-50">{" "}excl. VAT</small>
                    </span>
                  </span>
                  <div className="flex items-center justify-between gap-2 w-full mt-2">
                    <span className="text-2xl font-bold line-through font-serif text-muted-foreground">
                      ${pricing.originalPrice}
                    </span>
                    {showCampaign && tier.campaign && !CAMPAIGNS[tier.campaign].isGlobal && (
                      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-400/30 px-3 py-1.5 rounded-sm flex items-center gap-1.5 w-fit">
                        <Tag className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        <span className="text-blue-700 dark:text-blue-300 font-semibold text-xs">{CAMPAIGNS[tier.campaign].description}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
              {!pricing.hasDiscount && (
                <span className="text-5xl font-bold font-serif text-foreground flex items-center">
                  ${tier.price}<span className="flex flex-col ml-2"><span className="text-base opacity-75">/{tier.billingPeriod || 'month'}

                  </span>
                    <small className="text-xs opacity-50">{" "}excl. VAT</small>
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* CTA Button - pinned to bottom */}
        <div className="mt-auto pt-6">
          <div className="mb-8">
            {/* Divider for recommended */}
            <div className="flex items-center gap-4 py-2">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Includes</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            </div>

            {/* Features list */}
            <div className="space-y-3 pt-2">
              {tier.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 shrink-0 mt-0.5 text-primary" />
                  <p className="text-sm text-foreground">
                    {feature}
                    {feature.includes("Credits") && (
                      <span className="block text-xs text-muted-foreground">{tier.overage}</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
          {useCTAButton ? (
            <CTAButton
              to={ctaLink}
              size="default"
              variant={variant}
              event={`${tier.name} CTA Button`}
              buttonText={ctaText}
              microcopy=""
              className="w-full"
            />
          ) : (
            <Button
              onClick={onCtaClick}
              variant={variant}
              size="default"
              className="w-full"
            >
              {ctaText}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
